/**
 * Money-unit conversions shared by the server and the client.
 *
 * Dependency-free on purpose — no paraglide, no `db`, no `env` — so a backend
 * module can import it. `shared/format.ts` cannot be that module: it imports the
 * Paraglide runtime, which is client-only and is never emitted into `dist-server`,
 * so a backend import of it builds and type-checks cleanly and then fails to boot.
 */

/**
 * Microcents as a EUR AMOUNT, for arithmetic and form fields rather than display.
 * 1 EUR = 100,000,000 micro. The one place that factor is written for the client,
 * so a screen cannot come to disagree with the formatters by a power of ten.
 */
export function microToEurAmount(micro: number): number {
	return micro / 100_000_000;
}

/**
 * Microcents as an EXACT euro amount for a file: every stored digit, comma decimal, no
 * grouping — `123456789012n` → `"1234,56789012"`. 1 EUR = 100,000,000 micro, so eight
 * decimals hold the stored value exactly.
 *
 * Integer arithmetic only. A `number` division would round at the 16th significant digit
 * and a `toFixed` would round to its places; this prints what is stored, so lines summed
 * from a file add up to the stored total to the last unit.
 */
export function microToExactEurText(micro: bigint): string {
	const negative = micro < 0n;
	const magnitude = negative ? -micro : micro;
	const euros = magnitude / 100_000_000n;
	const fraction = (magnitude % 100_000_000n).toString().padStart(8, "0");
	return `${negative ? "-" : ""}${euros},${fraction}`;
}
